import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

const SecureOTPForgotPassword = () => {
    const [step, setStep] = useState(1);
    const [method, setMethod] = useState('email'); // 'email' or 'phone'
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [firebaseToken, setFirebaseToken] = useState(null);

    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();

    const queryParams = new URLSearchParams(location.search);
    const role = queryParams.get('role') || 'admin';
    const isDispatcher = role === 'dispatcher';
    
    // Theme colors
    const primaryColor = isDispatcher ? 'teal' : 'rose';
    const gradientFrom = isDispatcher ? 'from-emerald-600' : 'from-rose-600';
    const gradientTo = isDispatcher ? 'to-teal-600' : 'to-orange-600';
    const hoverFrom = isDispatcher ? 'hover:from-emerald-700' : 'hover:from-rose-700';
    const hoverTo = isDispatcher ? 'hover:to-teal-700' : 'hover:to-orange-700';

    useEffect(() => {
        if (!window.recaptchaVerifier) {
            try {
                window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
                    size: 'invisible'
                });
                window.recaptchaVerifier.render().catch(console.error);
            } catch (e) {
                console.error('Recaptcha init error', e);
            }
        }
    }, []);

    const getPasswordStrength = (pw) => {
        let score = 0;
        if (!pw) return score;
        if (pw.length > 8) score += 1;
        if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
        if (/\d/.test(pw)) score += 1;
        if (/[^A-Za-z0-9]/.test(pw)) score += 1;
        return score; // max 4
    };

    const handleSendOTP = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            if (method === 'email') {
                const { data } = await api.post('/auth/forgot-password-otp', { email, role, method: 'email' });
                setSuccess(data.message);
                setStep(2);
            } else {
                if (!phone) {
                    setError('Phone number is required for SMS recovery');
                    setLoading(false);
                    return;
                }

                // E.164 Phone Normalization
                let raw = phone.trim();
                let digits = raw.replace(/[^\d]/g, '');
                let e164Phone = '';
                
                if (digits.startsWith('44')) {
                    e164Phone = '+' + digits;
                } else if (digits.startsWith('07')) {
                    e164Phone = '+44' + digits.substring(1);
                } else if (digits.startsWith('92')) {
                    e164Phone = '+' + digits;
                } else if (digits.startsWith('03')) {
                    e164Phone = '+92' + digits.substring(1);
                } else {
                    e164Phone = raw.startsWith('+') ? raw : '+' + digits;
                }
                
                // 1. Verify user exists and has this phone number on backend first
                const { data } = await api.post('/auth/forgot-password-otp', { phone: e164Phone, role, method: 'phone' });
                
                // 2. Trigger Firebase SMS
                try {
                    if (!window.recaptchaVerifier) {
                        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
                        await window.recaptchaVerifier.render();
                    }
                    const result = await signInWithPhoneNumber(auth, e164Phone, window.recaptchaVerifier);
                    setConfirmationResult(result);
                    setSuccess('SMS sent successfully to ' + data.maskedPhone);
                    setStep(2);
                } catch (fbErr) {
                    console.error('Firebase SMS error:', fbErr);
                    setError('Failed to send SMS. Please check your phone number format (include country code e.g. +44).');
                    if (window.recaptchaVerifier) {
                        window.recaptchaVerifier.clear();
                        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
                    }
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to request OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        setError('');
        
        if (otp.length < 6) {
            return setError('Please enter a valid 6-digit OTP');
        }

        setLoading(true);
        try {
            if (method === 'phone') {
                const result = await confirmationResult.confirm(otp);
                const token = await result.user.getIdToken();
                setFirebaseToken(token);
                setSuccess('Phone verified successfully!');
                setStep(3);
            } else {
                // For email, we verify on the final step with the password
                setSuccess('Email OTP accepted. Proceed to set new password.');
                setStep(3);
            }
        } catch (err) {
            setError(err.message || 'Invalid OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            return setError('Passwords do not match');
        }

        const strength = getPasswordStrength(newPassword);
        if (strength < 4) {
            return setError('Password must contain uppercase, lowercase, number, and special character');
        }

        setLoading(true);
        try {
            const payload = {
                ...(method === 'email' ? { email } : { phone }),
                role,
                method,
                newPassword
            };
            if (method === 'email') payload.otp = otp;
            if (method === 'phone') payload.firebaseToken = firebaseToken;

            const { data } = await api.post('/auth/reset-password-otp', payload);
            setSuccess('Password reset successfully! Redirecting...');
            
            setTimeout(() => {
                navigate(`/${role}/login`);
            }, 2000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="h-screen w-screen flex bg-slate-50 dark:bg-[#050510] overflow-hidden font-['Inter',sans-serif] transition-colors duration-500">
            <div id="recaptcha-container"></div>
            
            {/* ── Left Panel ───────── */}
            <div className="hidden lg:flex lg:w-[45%] xl:w-[50%] relative group overflow-hidden">
                <div className="absolute inset-0 bg-slate-900 dark:bg-black transition-colors duration-500">
                    <img
                        src="/assets/login-bg.png"
                        alt="IdaCab Premium Transport"
                        className="w-full h-full object-cover opacity-[0.85] dark:opacity-40 grayscale-[10%] group-hover:scale-105 transition-transform duration-[20s] ease-out mix-blend-overlay"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent transition-colors duration-500" />
                </div>
                
                <div className="relative z-10 flex flex-col justify-between p-16 w-full h-full border-r border-slate-200 dark:border-white/5 transition-colors">
                    <div className="flex justify-between items-start">
                        <div className="bg-white/95 p-1.5 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] inline-flex items-center justify-center backdrop-blur-sm">
                            <img src="/assets/logo.png" alt="IdaCab Logo" className="h-16 sm:h-24 w-auto object-contain" />
                        </div>
                        <div className="px-4 py-1.5 rounded-full bg-white/10 dark:bg-white/5 backdrop-blur-md border border-white/20 dark:border-white/10 text-white dark:text-white/90 text-[10px] font-black tracking-widest uppercase shadow-xl transition-all">
                            Secure Recovery
                        </div>
                    </div>

                    <div className="max-w-lg mb-12">
                        <h1 className="text-5xl lg:text-6xl font-black text-white leading-[1.1] mb-6 tracking-tight drop-shadow-2xl">
                            Recover Your
                            <span className={`block text-${primaryColor}-400`}>Account</span>
                        </h1>
                        <p className="text-white/90 text-lg leading-relaxed font-medium drop-shadow-md">
                            Verify your identity using our highly secure OTP system to regain access to the {role.toUpperCase()} portal.
                        </p>
                    </div>

                    <div className="flex items-center gap-6 text-white/50 text-[11px] font-black tracking-widest uppercase">
                        <span className="flex items-center gap-2">Enterprise Security</span>
                        <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                        <span className="flex items-center gap-2">256-Bit Encrypted</span>
                    </div>
                </div>
            </div>

            {/* ── Right Panel Form ────────── */}
            <div className="flex-1 h-full relative z-10 flex flex-col justify-center items-center p-4 sm:p-8 lg:p-20 overflow-hidden">
                <div className="absolute top-6 right-6 z-50">
                    <button onClick={toggleTheme} className="p-3 rounded-full bg-white/50 dark:bg-slate-800/50 backdrop-blur-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all border border-slate-200 dark:border-slate-700 shadow-sm">
                        {theme === 'light' ? '🌙' : '☀️'}
                    </button>
                </div>

                <div className="w-full max-w-[440px] bg-white dark:bg-[#0B0F19] p-8 sm:p-10 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.5)] border border-slate-100 dark:border-white/5 relative z-10 transition-colors">
                    
                    <div className="text-center mb-8">
                        <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight uppercase">Secure Recovery</h2>
                        <p className="text-sm font-bold text-slate-500 dark:text-slate-400 mt-2">Step {step} of 3</p>
                    </div>

                    {error && <div className="mb-6 p-4 bg-red-50 dark:bg-red-500/10 border-l-4 border-red-500 rounded-r-xl text-sm font-bold text-red-600 dark:text-red-400">{error}</div>}
                    {success && <div className="mb-6 p-4 bg-green-50 dark:bg-green-500/10 border-l-4 border-green-500 rounded-r-xl text-sm font-bold text-green-600 dark:text-green-400">{success}</div>}

                    {/* Step 1: Send OTP */}
                    {step === 1 && (
                        <form onSubmit={handleSendOTP} className="space-y-5">
                            <div className="flex justify-center gap-4 mb-6">
                                <button type="button" onClick={() => setMethod('email')} className={`px-4 py-2 text-xs font-bold rounded-xl uppercase tracking-wider transition-all ${method === 'email' ? `bg-${primaryColor}-100 dark:bg-${primaryColor}-900/30 text-${primaryColor}-600 dark:text-${primaryColor}-400 border border-${primaryColor}-500/20` : 'bg-slate-100 dark:bg-slate-800 text-slate-500 border border-transparent'}`}>Email OTP</button>
                                <button type="button" onClick={() => setMethod('phone')} className={`px-4 py-2 text-xs font-bold rounded-xl uppercase tracking-wider transition-all ${method === 'phone' ? `bg-${primaryColor}-100 dark:bg-${primaryColor}-900/30 text-${primaryColor}-600 dark:text-${primaryColor}-400 border border-${primaryColor}-500/20` : 'bg-slate-100 dark:bg-slate-800 text-slate-500 border border-transparent'}`}>Phone OTP</button>
                            </div>
                            
                            {method === 'email' && (
                                <div>
                                    <label className="block text-[11px] font-black text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-widest">Registered Email</label>
                                    <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200 dark:focus:ring-slate-700" placeholder={isDispatcher ? "dispatch@idacab.com" : "admin@idacab.com"} />
                                </div>
                            )}

                            {method === 'phone' && (
                                <div>
                                    <label className="block text-[11px] font-black text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-widest">Phone Number (with country code)</label>
                                    <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200 dark:focus:ring-slate-700" placeholder="+447123456789" />
                                </div>
                            )}

                            <button type="submit" disabled={loading} className={`w-full py-4 px-4 rounded-2xl bg-gradient-to-r ${gradientFrom} ${gradientTo} ${hoverFrom} ${hoverTo} text-white font-black text-xs uppercase tracking-[0.2em] shadow-xl transition-all flex justify-center`}>
                                {loading ? 'SENDING...' : 'SEND OTP'}
                            </button>
                        </form>
                    )}

                    {/* Step 2: Verify OTP */}
                    {step === 2 && (
                        <form onSubmit={handleVerifyOTP} className="space-y-5">
                            <div>
                                <label className="block text-[11px] font-black text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-widest">Enter 6-Digit OTP</label>
                                <input type="text" required maxLength="6" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-3 text-center text-2xl tracking-[0.5em] font-black rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none" placeholder="000000" />
                            </div>
                            <button type="submit" disabled={loading || otp.length !== 6} className={`w-full py-4 px-4 rounded-2xl bg-gradient-to-r ${gradientFrom} ${gradientTo} ${hoverFrom} ${hoverTo} text-white font-black text-xs uppercase tracking-[0.2em] shadow-xl transition-all flex justify-center`}>
                                {loading ? 'VERIFYING...' : 'VERIFY OTP'}
                            </button>
                        </form>
                    )}

                    {/* Step 3: New Password */}
                    {step === 3 && (
                        <form onSubmit={handleResetPassword} className="space-y-5">
                            <div>
                                <label className="block text-[11px] font-black text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-widest">New Password</label>
                                <div className="relative">
                                    <input type={showPassword ? 'text' : 'password'} required value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder:text-slate-400 text-sm focus:outline-none" placeholder="••••••••" />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-3 text-slate-400">👁</button>
                                </div>
                                <div className="flex gap-1 mt-2">
                                    {[1, 2, 3, 4].map(idx => (
                                        <div key={idx} className={`h-1 flex-1 rounded-full ${getPasswordStrength(newPassword) >= idx ? `bg-${primaryColor}-500` : 'bg-slate-200 dark:bg-slate-700'}`} />
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-[11px] font-black text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-widest">Confirm Password</label>
                                <input type={showPassword ? 'text' : 'password'} required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder:text-slate-400 text-sm focus:outline-none" placeholder="••••••••" />
                            </div>
                            <button type="submit" disabled={loading} className={`w-full py-4 px-4 rounded-2xl bg-gradient-to-r ${gradientFrom} ${gradientTo} ${hoverFrom} ${hoverTo} text-white font-black text-xs uppercase tracking-[0.2em] shadow-xl transition-all flex justify-center`}>
                                {loading ? 'SAVING...' : 'RESET PASSWORD'}
                            </button>
                        </form>
                    )}

                    <div className="mt-8 text-center">
                        <Link to={`/${role}/login`} className="text-xs font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                            ← BACK TO LOGIN
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SecureOTPForgotPassword;
